"use client";

import { Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLeadForm } from "@/lib/use-lead-form";
import type { LeadPayload } from "@/lib/api";

interface LeadFormProps {
  source: LeadPayload["source"];
  planInterest?: string;
  submitLabel?: string;
  showMessage?: boolean;
}

function LeadForm({ source, planInterest, submitLabel = "Submit", showMessage = true }: LeadFormProps) {
  const { status, feedback, fieldErrors, captcha, captchaAnswer, setCaptchaAnswer, captchaLoading, refreshCaptcha, handleSubmit } = useLeadForm(
    source,
    planInterest,
  );

  if (status === "success") {
    return (
      <div className="rounded-lg border border-success/30 bg-success-bg px-4 py-6 text-center text-sm text-success">
        {feedback}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" name="name" placeholder="Dr. Asha Verma" aria-invalid={!!fieldErrors.name} />
          {fieldErrors.name && <p className="text-xs text-danger">{fieldErrors.name}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email" type="email" placeholder="you@yourclinic.com" aria-invalid={!!fieldErrors.email} />
          {fieldErrors.email && <p className="text-xs text-danger">{fieldErrors.email}</p>}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" name="phone" type="tel" placeholder="+91 98765 43210" aria-invalid={!!fieldErrors.phone} />
          {fieldErrors.phone && <p className="text-xs text-danger">{fieldErrors.phone}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="clinic_name">Clinic name</Label>
          <Input id="clinic_name" name="clinic_name" placeholder="Apollo Clinic" />
        </div>
      </div>
      {showMessage && (
        <div className="space-y-1.5">
          <Label htmlFor="message">How can we help?</Label>
          <Textarea id="message" name="message" placeholder="Tell us a bit about your clinic..." />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="captcha_answer">Enter the code shown below</Label>
        <div className="flex items-center gap-2">
          <div className="flex h-[46px] w-[150px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-input bg-muted">
            {captcha ? (
              // eslint-disable-next-line @next/next/no-img-element -- server-generated data URI, not an optimizable asset.
              <img src={captcha.image} alt="Verification code" className="h-full w-full object-cover" />
            ) : (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={refreshCaptcha} disabled={captchaLoading} aria-label="Get a new code">
            <RefreshCw className={captchaLoading ? "size-4 animate-spin" : "size-4"} />
          </Button>
          <Input
            id="captcha_answer"
            value={captchaAnswer}
            onChange={(e) => setCaptchaAnswer(e.target.value)}
            placeholder="Code"
            className="flex-1"
            aria-invalid={!!fieldErrors.captcha_answer}
          />
        </div>
        {fieldErrors.captcha_answer && <p className="text-xs text-danger">{fieldErrors.captcha_answer}</p>}
      </div>

      {status === "error" && feedback && !fieldErrors.captcha_answer && <p className="text-sm text-danger">{feedback}</p>}

      <Button type="submit" className="w-full" disabled={status === "submitting"}>
        {status === "submitting" && <Loader2 className="size-4 animate-spin" />}
        {submitLabel}
      </Button>
    </form>
  );
}

export { LeadForm };
